import type {
  InitialIngredientQuantities,
  ModificationGroupLimits,
  ModificationsMap,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the upsell price badge value for an ingredient:
 * - Extras-only ingredients always show their addition cost.
 * - Included ingredients show their own addition cost while the group has
 *   room, and the paired extras cost once the group is at capacity.
 */
export function getUpsellPrice(params: Params): number | undefined {
  const {
    ingredientId,
    allModificationsMap,
    extrasModificationsMap,
    modificationLimits,
    includedGroupCounts,
    initialIngredientQuantities,
  } = params;

  if (!ingredientId || !allModificationsMap) return undefined;

  const modification = allModificationsMap.get(ingredientId);

  if (!modification?.ingredientId) return undefined;

  // Extras-only ingredient — always show its cost.
  if (modification.isExtra) return modification.additionCost;

  const isInitialIngredient = initialIngredientQuantities.has(ingredientId);
  const extrasModification = extrasModificationsMap.get(modification.ingredientId);

  if (!extrasModification) return undefined;

  const groupLimits = modificationLimits[modification.modificationGroupName];

  if (!groupLimits) return undefined;

  const activeCount = includedGroupCounts[modification.modificationGroupName] ?? 0;

  // Group has room — initial ingredients are free, others show their addition cost.
  if (activeCount < groupLimits.maxAggregateQuantity) {
    return isInitialIngredient ? undefined : modification.additionCost || undefined;
  }

  // Group at capacity — show the paired extras cost.
  return extrasModification.additionCost;
}

// ─── Types ──────────────────────────────────────────────────────────────────

type Params = {
  ingredientId: string | undefined;
  allModificationsMap: ModificationsMap | undefined;
  extrasModificationsMap: ModificationsMap;
  modificationLimits: ModificationGroupLimits;
  includedGroupCounts: Record<string, number>;
  initialIngredientQuantities: InitialIngredientQuantities;
};
